import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { getPurchaseRequests, savePurchaseRequest, getNextOrderNo } from "../../components/Api/GmsPurchaseRequest";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getSuppliers, searchSuppliers } from "../../components/Api/GmsSupplier";
import { getProducts } from "../../components/Api/GmsProduct";
import { getActiveUnits } from "../../components/Api/GmsUnits";
import { getTaxes } from "../../components/Api/GmsTax";
import { Eye, SquarePen, Plus, Minus } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  PurchaseRequest_Id: 0,
  PurchaseRequest_OrderNo: "",
  PurchaseRequest_RefNo: "",
  PurchaseRequest_Supplier_Id: null,
  PurchaseRequest_InvoiceType_Id: 1, // 1 = Cash, 2 = Credit
  PurchaseRequest_Date: new Date(),
  PurchaseRequest_DueDate: new Date(),
  PurchaseRequest_ArrivalDate: null,
  PurchaseRequest_Amount: 0,
  PurchaseRequest_DiscountPercent: 0,
  PurchaseRequest_DiscountAmount: 0,
  PurchaseRequest_OtherAmount: 0,
  PurchaseRequest_TaxAmount: 0,
  PurchaseRequest_NetAmount: 0,
  PurchaseRequest_Notes: "",
  PurchaseRequest_TotalQty: 0,
  PurchaseRequest_Branch_Id: null,
  PurchaseRequest_TotalAmount: 0,
  PurchaseRequest_CreatedDate: new Date().toISOString(),
  PurchaseRequest_CreatedBy: 1,
  PurchaseRequest_Business_Id: null,
  PurchaseRequest_Is_Received: false,
  Details: [],
};

const emptyFilter = {
  PurchaseRequest_Id: null,
  PurchaseRequest_OrderNo: null,
  PurchaseRequest_Supplier_Id: null,
  PurchaseRequest_Business_Id: null,
  From_Date: null,
  To_Date: null,
};

const defaultDetail = {
  PurchaseRequestDet_Id: 0,
  PurchaseRequestDet_Product_Id: null,
  PurchaseRequestDet_Unit_Id: 1,
  PurchaseRequestDet_Quantity: 1,
  PurchaseRequestDet_BonusQuantity: 0,
  PurchaseRequestDet_UnitPrice: 0,
  PurchaseRequestDet_DiscountPercent: 0,
  PurchaseRequestDet_TotalAmount: 0,
  PurchaseRequestDet_NetAmount: 0,
  PurchaseRequestDet_TaxPercent: 0,
  Product_NameEn: "",
  Unit_NameEn: "",
  TaxDetails: [],
};

// Rounding helper: standard half-up to 2 decimals
const roundHalfUp = (value, decimals = 2) => {
  const factor = Math.pow(10, decimals);
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
};

// Format date as dd/MM/yyyy
const formatDateDDMMYYYY = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
  };
};

export default function PurchaseRequestPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsPurchaseOrder');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsPurchaseOrder');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsPurchaseOrder');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsPurchaseOrder');
  const isAdmin = isUserAdmin();

  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [details, setDetails] = useState([defaultDetail]);
  
  const [isEditing, setIsEditing] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [formKey, setFormKey] = useState(0);
  
  // Supplier search states
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [supplierSearchTimeout, setSupplierSearchTimeout] = useState(null);
  const [selectedSupplierOption, setSelectedSupplierOption] = useState(null);
  const [selectedSupplierFilterOption, setSelectedSupplierFilterOption] = useState(null);

  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
      loadBranches();
      loadSuppliers();
      loadProducts();
      loadUnits();
      loadTaxes();
      loadPurchaseRequests();
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadPurchaseRequests();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({});
      const businessData = Array.isArray(data) ? data : (data.data || []);
      setBusinesses(businessData);
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load companies", "error");
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({});
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await getSuppliers({});
      setSuppliers(data || []);
    } catch (error) {
      console.error("Error loading suppliers:", error);
      Swal.fire("Error", "Failed to load suppliers", "error");
    }
  };

  // Manual search function for supplier search
  const searchSuppliersManually = async (inputValue) => {
    // Trim whitespace from input
    const trimmedInput = inputValue ? inputValue.trim() : '';
    
    if (!trimmedInput || trimmedInput.length < 3) {
      setSupplierSearchOptions([]);
      return;
    }
    
    try {
      setSupplierSearchLoading(true);
      const response = await searchSuppliers(trimmedInput, 1, 20);
      
      if (!response || !response.items || !Array.isArray(response.items)) {
        setSupplierSearchOptions([]);
        return;
      }
      
      const formattedOptions = response.items.map(supplier => {
        // Check if search text appears in Arabic name (handle Arabic text properly)
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';
        
        // Check if search text contains Arabic characters or matches Arabic name
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) || 
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );
        
        // Prioritize Arabic name if search matches Arabic, otherwise show English first
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          // Search matched Arabic name - show Arabic first
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          // Search matched English name or code - show English first
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          // Only Arabic name available
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }
        
        // Create label with both names if available
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || supplier.supplier_ContactNo || 'N/A'}`;
        
        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });
      
      setSupplierSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching suppliers:', error);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  // Manual search function for supplier filter search
  const searchSuppliersFilterManually = async (inputValue) => {
    // Trim whitespace from input
    const trimmedInput = inputValue ? inputValue.trim() : '';
    
    if (!trimmedInput || trimmedInput.length < 3) {
      setSupplierSearchOptions([]);
      return;
    }
    
    try {
      setSupplierSearchLoading(true);
      const response = await searchSuppliers(trimmedInput, 1, 20);
      
      if (!response || !response.items || !Array.isArray(response.items)) {
        setSupplierSearchOptions([]);
        return;
      }
      
      const formattedOptions = response.items.map(supplier => {
        // Check if search text appears in Arabic name (handle Arabic text properly)
        const hasArabicName = supplier.supplier_NameAr && supplier.supplier_NameAr.trim() !== '';
        const hasEnglishName = supplier.supplier_NameEn && supplier.supplier_NameEn.trim() !== '';
        
        // Check if search text contains Arabic characters or matches Arabic name
        const containsArabicChars = /[\u0600-\u06FF]/.test(trimmedInput);
        const searchInArabic = hasArabicName && (
          supplier.supplier_NameAr.includes(trimmedInput) || 
          (containsArabicChars && supplier.supplier_NameAr.includes(trimmedInput))
        );
        
        // Prioritize Arabic name if search matches Arabic, otherwise show English first
        let primaryName, secondaryName;
        if (searchInArabic && hasArabicName) {
          // Search matched Arabic name - show Arabic first
          primaryName = supplier.supplier_NameAr;
          secondaryName = hasEnglishName ? supplier.supplier_NameEn : null;
        } else if (hasEnglishName) {
          // Search matched English name or code - show English first
          primaryName = supplier.supplier_NameEn;
          secondaryName = hasArabicName ? supplier.supplier_NameAr : null;
        } else if (hasArabicName) {
          // Only Arabic name available
          primaryName = supplier.supplier_NameAr;
          secondaryName = null;
        } else {
          primaryName = 'N/A';
          secondaryName = null;
        }
        
        // Create label with both names if available
        let label = primaryName;
        if (secondaryName && secondaryName.trim() !== '') {
          label = `${primaryName} — ${secondaryName}`;
        }
        label += ` — ${supplier.supplier_Code || supplier.supplier_ContactNo || 'N/A'}`;
        
        return {
          value: supplier.supplier_Id,
          label: label,
          supplier: supplier
        };
      });
      
      setSupplierSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching suppliers:', error);
      setSupplierSearchOptions([]);
    } finally {
      setSupplierSearchLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts({});
      setProducts(data || []);
    } catch (error) {
      console.error("Error loading products:", error);
      Swal.fire("Error", "Failed to load products", "error");
    }
  };

  const loadUnits = async () => {
    try {
      const data = await getActiveUnits();
      setUnits(data || []);
    } catch (error) {
      console.error("Error loading units:", error);
      Swal.fire("Error", "Failed to load units", "error");
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ tax_Active: true });
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
      Swal.fire("Error", "Failed to load taxes", "error");
    }
  };

  const loadPurchaseRequests = async () => {
    try {
      setLoading(true);
      const data = await getPurchaseRequests(filter);
      setPurchaseRequests(data || []);
      setTotalCount(data?.length || 0);
    } catch (error) {
      console.error("Error loading purchase requests:", error);
      Swal.fire("Error", "Failed to load purchase requests", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessChange = async (businessId) => {
    setForm(prev => ({
      ...prev,
      PurchaseRequest_Business_Id: businessId,
      PurchaseRequest_Branch_Id: null
    }));

    if (businessId) {
      try {
        const result = await getNextOrderNo(businessId);
        if (result.nextOrderNo) {
          setForm(prev => ({
            ...prev,
            PurchaseRequest_OrderNo: result.nextOrderNo
          }));
        }
      } catch (error) {
        console.error("Error getting next order number:", error);
      }
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDetailChange = async (index, field, value) => {
    // Validate quantity limits
    if (field === 'PurchaseRequestDet_Quantity') {
      if (value > 5000) {
        Swal.fire({
          icon: 'warning',
          title: 'Quantity Limit Exceeded',
          text: 'Maximum quantity allowed is 5000. Please enter a value between 0 and 5000.',
          confirmButtonText: 'OK'
        });
        value = 5000;
      }
    } else if (field === 'PurchaseRequestDet_BonusQuantity') {
      if (value > 1000) {
        Swal.fire({
          icon: 'warning',
          title: 'Bonus Quantity Limit Exceeded',
          text: 'Maximum bonus quantity allowed is 1000. Please enter a value between 0 and 1000.',
          confirmButtonText: 'OK'
        });
        value = 1000;
      }
    }
    
    const newDetails = [...details];
    newDetails[index] = { ...newDetails[index], [field]: value };
    
    // Auto-fetch product details
    if (field === 'PurchaseRequestDet_Product_Id' && value) {
      const product = products.find(p => p.product_Id === value);
      if (product) {
        const unit = units.find(u => u.value === product.unit_Id);
        const unitName = unit ? unit.label.split(' - ')[1] || unit.label : "Unit";
        
        // Calculate tax percentage from taxIds
        let taxPercent = 0;
        let taxDetails = [];
        if (product.taxIds && product.taxIds.length > 0) {
          const productTaxes = taxes.filter(tax => product.taxIds.includes(tax.tax_Id));
          taxPercent = productTaxes.reduce((sum, tax) => sum + (tax.tax_Perc || 0), 0);
          taxDetails = productTaxes.map(tax => ({
            PurchaseRequestTaxDet_Tax_Id: tax.tax_Id,
            Tax_NameEn: tax.tax_NameEn,
            Tax_Perc: tax.tax_Perc
          }));
        }
        
        newDetails[index] = {
          ...newDetails[index],
          PurchaseRequestDet_UnitPrice: product.product_CostPrice || 0,
          PurchaseRequestDet_TaxPercent: taxPercent,
          PurchaseRequestDet_Unit_Id: product.unit_Id || 1,
          Product_NameEn: product.product_NameEn,
          Unit_NameEn: unitName,
          TaxDetails: taxDetails
        };
      }
    }
    
    // Calculate amounts
    if (field === 'PurchaseRequestDet_Quantity' || field === 'PurchaseRequestDet_BonusQuantity' || 
        field === 'PurchaseRequestDet_UnitPrice' || field === 'PurchaseRequestDet_DiscountPercent' ||
        field === 'PurchaseRequestDet_TaxPercent') {
      calculateDetailAmounts(newDetails, index);
      
      if (isEditing) {
        recalculateTotalsFromDetails(newDetails);
      }
    }
    
    setDetails(newDetails);
  };

  const recalculateTotalsFromDetails = (detailsArray) => {
    let totalAmount = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    
    detailsArray.forEach((detail) => {
      if (detail && detail.PurchaseRequestDet_Product_Id) {
        const totalAmountValue = parseFloat(detail.PurchaseRequestDet_TotalAmount) || 0;
        const discountAmount = parseFloat(detail.PurchaseRequestDet_DiscountAmount) || 0;
        const taxAmount = parseFloat(detail.PurchaseRequestDet_TaxAmount) || 0;
        
        totalAmount += totalAmountValue;
        totalDiscount += discountAmount;
        totalTax += taxAmount;
      }
    });
    
    totalAmount = roundHalfUp(totalAmount, 2);
    totalDiscount = roundHalfUp(totalDiscount, 2);
    totalTax = roundHalfUp(totalTax, 2);

    const netAmount = roundHalfUp(totalAmount + totalTax, 2);
    
    setForm(prev => ({
      ...prev,
      PurchaseRequest_TotalAmount: totalAmount,
      PurchaseRequest_DiscountAmount: totalDiscount,
      PurchaseRequest_TaxAmount: totalTax,
      PurchaseRequest_NetAmount: netAmount
    }));
  };

  const calculateDetailAmounts = (detailsArray, index) => {
    const detail = detailsArray[index];
    const quantity = parseFloat(detail.PurchaseRequestDet_Quantity) || 0;
    const bonusQuantity = parseFloat(detail.PurchaseRequestDet_BonusQuantity) || 0;
    const unitPrice = parseFloat(detail.PurchaseRequestDet_UnitPrice) || 0;
    const discountPercent = parseFloat(detail.PurchaseRequestDet_DiscountPercent) || 0;
    const taxPercent = parseFloat(detail.PurchaseRequestDet_TaxPercent) || 0;

    if (quantity <= 0) {
      detailsArray[index] = {
        ...detail,
        PurchaseRequestDet_DiscountAmount: 0,
        PurchaseRequestDet_TotalAmount: 0,
        PurchaseRequestDet_TaxAmount: 0,
        PurchaseRequestDet_NetAmount: 0
      };
      return;
    }

    const discountedUnitPrice = roundHalfUp(unitPrice * (1 - (discountPercent / 100)), 2);
    const discountAmount = roundHalfUp(quantity * unitPrice * (discountPercent / 100), 2);
    const lineTotalAmount = roundHalfUp(quantity * discountedUnitPrice, 2);
    const totalQuantity = quantity + bonusQuantity;
    const taxBase = totalQuantity * discountedUnitPrice;

    let taxComponentsSum = 0;
    if (Array.isArray(detail.TaxDetails) && detail.TaxDetails.length > 0) {
      taxComponentsSum = detail.TaxDetails.reduce((sum, t) => {
        const perc = parseFloat(t.Tax_Perc || t.tax_Perc || 0) || 0;
        const comp = roundHalfUp(taxBase * (perc / 100), 2);
        return sum + comp;
      }, 0);
    } else {
      taxComponentsSum = roundHalfUp(taxBase * (taxPercent / 100), 2);
    }
    const taxAmount = roundHalfUp(taxComponentsSum, 2);
    const netAmount = roundHalfUp(lineTotalAmount + taxAmount, 2);

    detailsArray[index] = {
      ...detail,
      PurchaseRequestDet_DiscountAmount: discountAmount,
      PurchaseRequestDet_TotalAmount: lineTotalAmount,
      PurchaseRequestDet_TaxAmount: taxAmount,
      PurchaseRequestDet_NetAmount: netAmount
    };
  };

  const addDetail = () => {
    setDetails([...details, { ...defaultDetail }]);
  };

  const removeDetail = (index) => {
    if (details.length > 1) {
      const newDetails = details.filter((_, i) => i !== index);
      setDetails(newDetails);
    }
  };

  const calculateTotals = () => {
    if (isViewMode) {
      return;
    }

    let totalAmount = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    
    details.forEach((detail) => {
      if (detail && detail.PurchaseRequestDet_Product_Id) {
        const totalAmountValue = parseFloat(detail.PurchaseRequestDet_TotalAmount) || 0;
        const discountAmount = parseFloat(detail.PurchaseRequestDet_DiscountAmount) || 0;
        const taxAmount = parseFloat(detail.PurchaseRequestDet_TaxAmount) || 0;
        
        totalAmount += totalAmountValue;
        totalDiscount += discountAmount;
        totalTax += taxAmount;
      }
    });
    
    totalAmount = roundHalfUp(totalAmount, 2);
    totalDiscount = roundHalfUp(totalDiscount, 2);
    totalTax = roundHalfUp(totalTax, 2);

    const netAmount = roundHalfUp(totalAmount + totalTax, 2);
    
    setForm(prev => ({
      ...prev,
      PurchaseRequest_TotalAmount: totalAmount,
      PurchaseRequest_DiscountAmount: totalDiscount,
      PurchaseRequest_TaxAmount: totalTax,
      PurchaseRequest_NetAmount: netAmount
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [details]);

  const handleSave = async () => {
    try {
      if (!form.PurchaseRequest_Business_Id) {
        Swal.fire("Error", "Please select a company", "error");
        return;
      }
      
      if (!form.PurchaseRequest_Supplier_Id) {
        Swal.fire("Error", "Please select a supplier", "error");
        return;
      }
      
      if (details.length === 0 || !details.some(detail => detail.PurchaseRequestDet_Product_Id)) {
        Swal.fire("Error", "Please add at least one product", "error");
        return;
      }

      const filteredDetails = details.filter(detail => detail.PurchaseRequestDet_Product_Id);
      if (filteredDetails.length === 0) {
        Swal.fire("Error", "Please add at least one product", "error");
        return;
      }

      setSaveLoading(true);
      const session = getUserSession();
      
      const saveData = {
        ...form,
        PurchaseRequest_OrderNo: form.PurchaseRequest_OrderNo,
        PurchaseRequest_CreatedBy: isEditing ? form.PurchaseRequest_CreatedBy : session.userId,
        PurchaseRequest_Is_Received: false, // Always set to false on insert/update
        Details: filteredDetails,
        TaxDetails: filteredDetails
          .filter(detail => detail.TaxDetails)
          .flatMap((detail, index) => {
            return detail.TaxDetails.map(tax => ({
              ...tax,
              TempDetailId: index + 1
            }));
          })
      };

      const result = await savePurchaseRequest(saveData);
      
      if (result && (result.id > 0 || result.PurchaseRequest_Id > 0)) {
        Swal.fire("Success", "Purchase request saved successfully!", "success");
        setSaveLoading(true);
        setForm({ 
          ...defaultForm,
          PurchaseRequest_Date: new Date(),
          PurchaseRequest_DueDate: new Date()
        });
        setDetails([{ ...defaultDetail }]);
        setIsEditing(false);
        setIsViewMode(false);
        setSelectedSupplierOption(null);
        setSupplierSearchOptions([]);
        setFormKey(prev => prev + 1);
        setTimeout(() => {
          loadPurchaseRequests();
          setSaveLoading(false);
        }, 200);
      } else {
        Swal.fire("Error", result.message || "Failed to save purchase request", "error");
      }
    } catch (error) {
      console.error("Error saving purchase request:", error);
      Swal.fire("Error", "Failed to save purchase request", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (purchaseRequest) => {
    setForm({
      PurchaseRequest_Id: purchaseRequest.purchaseRequest_Id,
      PurchaseRequest_OrderNo: purchaseRequest.purchaseRequest_OrderNo,
      PurchaseRequest_RefNo: purchaseRequest.purchaseRequest_RefNo,
      PurchaseRequest_Supplier_Id: purchaseRequest.purchaseRequest_Supplier_Id,
      PurchaseRequest_InvoiceType_Id: purchaseRequest.purchaseRequest_InvoiceType_Id,
      PurchaseRequest_Date: purchaseRequest.purchaseRequest_Date ? new Date(purchaseRequest.purchaseRequest_Date) : new Date(),
      PurchaseRequest_DueDate: purchaseRequest.purchaseRequest_DueDate ? new Date(purchaseRequest.purchaseRequest_DueDate) : new Date(),
      PurchaseRequest_ArrivalDate: purchaseRequest.purchaseRequest_ArrivalDate ? new Date(purchaseRequest.purchaseRequest_ArrivalDate) : null,
      PurchaseRequest_Amount: purchaseRequest.purchaseRequest_Amount,
      PurchaseRequest_DiscountPercent: purchaseRequest.purchaseRequest_DiscountPercent,
      PurchaseRequest_DiscountAmount: purchaseRequest.purchaseRequest_DiscountAmount,
      PurchaseRequest_OtherAmount: purchaseRequest.purchaseRequest_OtherAmount,
      PurchaseRequest_TaxAmount: purchaseRequest.purchaseRequest_TaxAmount,
      PurchaseRequest_NetAmount: purchaseRequest.purchaseRequest_NetAmount,
      PurchaseRequest_Notes: purchaseRequest.purchaseRequest_Notes,
      PurchaseRequest_TotalQty: purchaseRequest.purchaseRequest_TotalQty,
      PurchaseRequest_Branch_Id: purchaseRequest.purchaseRequest_Branch_Id,
      PurchaseRequest_TotalAmount: purchaseRequest.purchaseRequest_TotalAmount,
      PurchaseRequest_CreatedDate: purchaseRequest.purchaseRequest_CreatedDate,
      PurchaseRequest_CreatedBy: purchaseRequest.purchaseRequest_CreatedBy,
      PurchaseRequest_Business_Id: purchaseRequest.purchaseRequest_Business_Id,
      PurchaseRequest_Is_Received: purchaseRequest.purchaseRequest_Is_Received || false,
      Supplier_NameEn: purchaseRequest.supplier_NameEn,
      InvoiceType_Name: purchaseRequest.invoiceType_Name
    });
    
    if (purchaseRequest.purchaseRequest_Supplier_Id && purchaseRequest.supplier_NameEn) {
      const supplier = suppliers.find(s => s.supplier_Id === purchaseRequest.purchaseRequest_Supplier_Id);
      const supplierCode = supplier?.supplier_Code || purchaseRequest.supplier_Code || purchaseRequest.supplier_ContactNo || '';
      setSelectedSupplierOption({
        value: purchaseRequest.purchaseRequest_Supplier_Id,
        label: `${purchaseRequest.supplier_NameEn} — ${supplierCode}`
      });
    } else {
      setSelectedSupplierOption(null);
    }
    
    if (purchaseRequest.details && purchaseRequest.details.length > 0) {
      const mappedDetails = purchaseRequest.details.map((detail) => ({
        PurchaseRequestDet_Id: detail.purchaseRequestDet_Id,
        PurchaseRequestDet_Product_Id: detail.purchaseRequestDet_Product_Id,
        PurchaseRequestDet_Unit_Id: detail.purchaseRequestDet_Unit_Id,
        PurchaseRequestDet_Quantity: detail.purchaseRequestDet_Quantity,
        PurchaseRequestDet_BonusQuantity: detail.purchaseRequestDet_BonusQuantity,
        PurchaseRequestDet_UnitPrice: detail.purchaseRequestDet_UnitPrice,
        PurchaseRequestDet_DiscountPercent: detail.purchaseRequestDet_DiscountPercent,
        PurchaseRequestDet_DiscountAmount: 0,
        PurchaseRequestDet_TotalAmount: 0,
        PurchaseRequestDet_NetAmount: 0,
        PurchaseRequestDet_TaxPercent: detail.purchaseRequestDet_TaxPercent,
        PurchaseRequestDet_TaxAmount: 0,
        Product_NameEn: detail.product_NameEn,
        Unit_NameEn: detail.unit_NameEn,
        TaxDetails: detail.taxDetails || []
      }));

      const calcDetails = [...mappedDetails];
      for (let i = 0; i < calcDetails.length; i++) {
        calculateDetailAmounts(calcDetails, i);
      }
      setDetails(calcDetails);
    } else {
      setDetails([{ ...defaultDetail }]);
    }
    
    setIsEditing(true);
    setIsViewMode(false);
    setActiveTab("1");
    window.scrollTo(0, 0);
  };

  const handleView = (purchaseRequest) => {
    handleEdit(purchaseRequest);
    setIsViewMode(true);
    setIsEditing(false);
  };

  const handleClear = () => {
    setForm({ 
      ...defaultForm,
      PurchaseRequest_Date: new Date(),
      PurchaseRequest_DueDate: new Date()
    });
    setDetails([{ ...defaultDetail }]);
    setIsEditing(false);
    setIsViewMode(false);
    setSelectedSupplierOption(null);
    setSupplierSearchOptions([]);
    setFormKey(prev => prev + 1);
  };

  const handleApplyFilter = () => {
    setPage(1);
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    setHasAppliedFilter(true);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Get paginated data for display
  const getPaginatedPurchaseRequests = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return purchaseRequests.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const paginatedData = getPaginatedPurchaseRequests();
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);

  // Options for dropdowns
  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: `${b.business_Code || ''} - ${b.business_NameEn || ''}`.trim()
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    if (!form.PurchaseRequest_Business_Id) return [];
    return branches
      .filter(b => b.branch_Buisness_Id === form.PurchaseRequest_Business_Id)
      .map(b => ({
        value: b.branch_Id,
        label: `${b.branch_Code || ''} - ${b.branch_NameEn || ''}`.trim()
      }));
  }, [branches, form.PurchaseRequest_Business_Id]);

  const productOptions = useMemo(() => {
    return products.map(p => ({
      value: p.product_Id,
      label: `${p.product_Code || ''} - ${p.product_NameEn || ''}`.trim()
    }));
  }, [products]);

  const invoiceTypeOptions = [
    { value: 1, label: 'Cash' },
    { value: 2, label: 'Credit' }
  ];

  if (!hasPermission) {
    return null;
  }

  const breadcrumbItem = activeTab === "1"
    ? (isEditing ? "Edit Purchase Order" : "Add Purchase Order")
    : "Purchase Order List";

  return (
    <div className="page-content">
      <style>{`
        input.no-spinner::-webkit-outer-spin-button,
        input.no-spinner::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input.no-spinner[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      <Container fluid>
        <Breadcrumb title="Purchase Order Management" breadcrumbItem={breadcrumbItem} />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>{isEditing ? "Edit Purchase Order" : "Add Purchase Order"}</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Purchase Order List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                        {/* Basic Information */}
                        <Row>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Company <span className="text-danger">*</span></Label>
                              <Select
                                key={`business-${formKey}`}
                                value={businessOptions.find(option => option.value === form.PurchaseRequest_Business_Id) || null}
                                onChange={(option) => handleBusinessChange(option?.value || null)}
                                options={businessOptions}
                                placeholder="Select Company"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={isViewMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Branch</Label>
                              <Select
                                key={`branch-${formKey}`}
                                value={branchOptions.find(option => option.value === form.PurchaseRequest_Branch_Id) || null}
                                onChange={(option) => handleFormChange("PurchaseRequest_Branch_Id", option?.value || null)}
                                options={branchOptions}
                                placeholder="Select Branch"
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={!form.PurchaseRequest_Business_Id || isViewMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Order Number</Label>
                              <Input
                                type="text"
                                value={form.PurchaseRequest_OrderNo}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Date <span className="text-danger">*</span></Label>
                              <DatePicker
                                selected={form.PurchaseRequest_Date}
                                onChange={(date) => handleFormChange("PurchaseRequest_Date", date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                disabled={isViewMode}
                              />
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Due Date</Label>
                              <DatePicker
                                selected={form.PurchaseRequest_DueDate}
                                onChange={(date) => handleFormChange("PurchaseRequest_DueDate", date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                disabled={isViewMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Arrival Date</Label>
                              <DatePicker
                                selected={form.PurchaseRequest_ArrivalDate}
                                onChange={(date) => handleFormChange("PurchaseRequest_ArrivalDate", date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                isClearable
                                disabled={isViewMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Reference Number</Label>
                              <Input
                                type="text"
                                value={form.PurchaseRequest_RefNo}
                                onChange={(e) => handleFormChange("PurchaseRequest_RefNo", e.target.value)}
                                placeholder="Enter reference number"
                                disabled={isViewMode}
                              />
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Invoice Type <span className="text-danger">*</span></Label>
                              <Select
                                key={`invoiceType-${formKey}`}
                                value={invoiceTypeOptions.find(option => option.value === form.PurchaseRequest_InvoiceType_Id) || null}
                                onChange={(option) => handleFormChange("PurchaseRequest_InvoiceType_Id", option?.value || 1)}
                                options={invoiceTypeOptions}
                                styles={selectStyles}
                                isDisabled={isViewMode}
                              />
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Supplier <span className="text-danger">*</span></Label>
                              <Select
                                key={`supplier-${formKey}`}
                                value={selectedSupplierOption}
                                onChange={(option) => {
                                  setSelectedSupplierOption(option);
                                  handleFormChange("PurchaseRequest_Supplier_Id", option?.value || null);
                                }}
                                options={supplierSearchOptions}
                                placeholder="Search by name..."
                                styles={selectStyles}
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                                isDisabled={isViewMode}
                                isLoading={supplierSearchLoading}
                                noOptionsMessage={() => "Type at least 3 characters"}
                                loadingMessage={() => "Searching..."}
                                filterOption={null}
                                onInputChange={(inputValue) => {
                                  if (supplierSearchTimeout) {
                                    clearTimeout(supplierSearchTimeout);
                                  }
                                  
                                  // Trim the input value before searching
                                  const trimmedValue = inputValue ? inputValue.trim() : '';
                                  
                                  const timeout = setTimeout(() => {
                                    if (trimmedValue.length >= 3) {
                                      searchSuppliersManually(trimmedValue);
                                    } else {
                                      setSupplierSearchOptions([]);
                                    }
                                  }, 300);
                                  setSupplierSearchTimeout(timeout);
                                  return inputValue;
                                }}
                              />
                            </div>
                          </Col>
                        </Row>

                        {/* Details Section */}
                        <Row>
                          <Col md={12}>
                            <Label className="fw-bold mb-3">Purchase Order Details</Label>
                            <div className="table-responsive">
                              <Table bordered style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th width="5%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}>Sr.No</th>
                                    <th width="20%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Product</th>
                                    <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Unit</th>
                                    <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Qty</th>
                                    <th width="8%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Bonus Qty</th>
                                    <th width="10%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Unit Price</th>
                                    <th width="6%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Discount %</th>
                                    <th width="6%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Tax %</th>
                                    <th width="12%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Total Amount</th>
                                    <th width="12%" style={{ padding: '8px 12px', border: '1px solid #dee2e6' }}>Net Amount</th>
                                    <th width="5%" style={{ padding: '8px 12px', border: '1px solid #dee2e6', textAlign: 'center' }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {details
                                    .filter(detail => detail && detail.PurchaseRequestDet_Quantity !== undefined)
                                    .map((detail, index) => (
                                    <tr key={index}>
                                      <td style={{ padding: '0', textAlign: 'center', border: '1px solid #dee2e6' }}>{index + 1}</td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Select
                                          key={`product-${formKey}-${index}`}
                                          value={productOptions.find(option => option.value === detail.PurchaseRequestDet_Product_Id) || null}
                                          onChange={(option) => handleDetailChange(index, 'PurchaseRequestDet_Product_Id', option?.value || null)}
                                          options={productOptions}
                                          placeholder="Select Product"
                                          styles={{
                                            ...selectStyles,
                                            control: (provided) => ({
                                              ...provided,
                                              border: 'none',
                                              borderRadius: '0',
                                              minHeight: '38px',
                                              boxShadow: 'none'
                                            })
                                          }}
                                          isClearable
                                          isSearchable
                                          maxMenuHeight={200}
                                          isDisabled={isViewMode}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="text"
                                          value={detail.Unit_NameEn || ''}
                                          readOnly
                                          style={{ border: 'none', backgroundColor: 'transparent', padding: '8px 12px' }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          className="no-spinner"
                                          min="0"
                                          step="1"
                                          value={detail.PurchaseRequestDet_Quantity || 0}
                                          onChange={(e) => handleDetailChange(index, 'PurchaseRequestDet_Quantity', parseFloat(e.target.value) || 0)}
                                          style={{ border: 'none', padding: '8px 12px' }}
                                          disabled={isViewMode}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          className="no-spinner"
                                          min="0"
                                          step="1"
                                          value={detail.PurchaseRequestDet_BonusQuantity || 0}
                                          onChange={(e) => handleDetailChange(index, 'PurchaseRequestDet_BonusQuantity', parseFloat(e.target.value) || 0)}
                                          style={{ border: 'none', padding: '8px 12px' }}
                                          disabled={isViewMode}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          className="no-spinner"
                                          min="0"
                                          step="0.01"
                                          value={detail.PurchaseRequestDet_UnitPrice || 0}
                                          onChange={(e) => handleDetailChange(index, 'PurchaseRequestDet_UnitPrice', parseFloat(e.target.value) || 0)}
                                          style={{ border: 'none', padding: '8px 12px' }}
                                          disabled={isViewMode}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          className="no-spinner"
                                          min="0"
                                          step="0.01"
                                          value={detail.PurchaseRequestDet_DiscountPercent || 0}
                                          onChange={(e) => handleDetailChange(index, 'PurchaseRequestDet_DiscountPercent', parseFloat(e.target.value) || 0)}
                                          style={{ border: 'none', padding: '8px 12px' }}
                                          disabled={isViewMode}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          className="no-spinner"
                                          min="0"
                                          step="0.01"
                                          value={detail.PurchaseRequestDet_TaxPercent || 0}
                                          readOnly
                                          style={{ border: 'none', backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          className="no-spinner"
                                          value={detail.PurchaseRequestDet_TotalAmount || 0}
                                          readOnly
                                          style={{ border: 'none', backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6' }}>
                                        <Input
                                          type="number"
                                          className="no-spinner"
                                          value={detail.PurchaseRequestDet_NetAmount || 0}
                                          readOnly
                                          style={{ border: 'none', backgroundColor: '#f8f9fa', padding: '8px 12px' }}
                                        />
                                      </td>
                                      <td style={{ padding: '0', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                        <div className="d-flex gap-1 justify-content-center">
                                          {!isViewMode && details.length > 1 && (
                                            <Button
                                              type="button"
                                              color="danger"
                                              size="sm"
                                              onClick={() => removeDetail(index)}
                                              style={{ 
                                                padding: '6px 10px',
                                                minWidth: '36px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px'
                                              }}
                                              title="Remove Row"
                                            >
                                              <Minus size={14} />
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                            {!isViewMode && (
                              <Button
                                type="button"
                                color="success"
                                size="sm"
                                onClick={addDetail}
                                className="mt-2"
                              >
                                <Plus size={16} /> Add Row
                              </Button>
                            )}
                          </Col>
                        </Row>

                        {/* Summary Section */}
                        <Row className="mt-4">
                          <Col md={6}>
                            <div className="mb-3">
                              <Label>Notes</Label>
                              <Input
                                type="textarea"
                                rows="3"
                                value={form.PurchaseRequest_Notes}
                                onChange={(e) => handleFormChange("PurchaseRequest_Notes", e.target.value)}
                                placeholder="Enter any notes"
                                disabled={isViewMode}
                              />
                            </div>
                          </Col>
                          <Col md={12}>
                            <Row>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Total Amount</Label>
                                  <Input
                                    type="number"
                                    value={form.PurchaseRequest_TotalAmount}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label>Total Discount Amount</Label>
                                  <Input
                                    type="number"
                                    value={form.PurchaseRequest_DiscountAmount}
                                    readOnly
                                    style={{ backgroundColor: '#f8f9fa' }}
                                  />
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="mb-3">
                                  <Label className="fw-bold">Net Amount</Label>
                                  <Input
                                    type="number"
                                    value={form.PurchaseRequest_NetAmount}
                                    readOnly
                                    style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}
                                  />
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        </Row>

                        {!isViewMode && (
                          <Row className="mt-4">
                            <Col>
                              <div className="d-flex gap-2">
                                <Button
                                  type="submit"
                                  color="primary"
                                  disabled={saveLoading}
                                  className="waves-effect waves-light"
                                >
                                  {saveLoading && (
                                    <ClipLoader size={16} color="#ffffff" className="me-2" />
                                  )}
                                  {isEditing ? "Update Purchase Order" : "Save Purchase Order"}
                                </Button>
                                <Button
                                  type="button"
                                  color="secondary"
                                  onClick={handleClear}
                                  className="waves-effect waves-light"
                                >
                                  Clear
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        )}

                        {isViewMode && (
                          <Row className="mt-4">
                            <Col>
                              <Button
                                type="button"
                                color="primary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add New Purchase Order
                              </Button>
                            </Col>
                          </Row>
                        )}
                      </Form>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Purchase Orders</h5>
                            <Button
                              color="primary"
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={3}>
                                <Label>Company</Label>
                                <Select
                                  value={businessOptions.find(option => option.value === filter.PurchaseRequest_Business_Id)}
                                  onChange={(option) => setFilter({ ...filter, PurchaseRequest_Business_Id: option?.value || null })}
                                  options={businessOptions}
                                  placeholder="Select Company"
                                  styles={selectStyles}
                                  isClearable
                                />
                              </Col>
                              <Col md={3}>
                                <Label>Supplier</Label>
                                <Select
                                  value={selectedSupplierFilterOption}
                                  onChange={(option) => {
                                    setSelectedSupplierFilterOption(option);
                                    setFilter({ ...filter, PurchaseRequest_Supplier_Id: option?.value || null });
                                  }}
                                  options={supplierSearchOptions}
                                  placeholder="Search by name..."
                                  styles={selectStyles}
                                  isClearable
                                  isSearchable
                                  isLoading={supplierSearchLoading}
                                  noOptionsMessage={() => "Type at least 3 characters"}
                                  loadingMessage={() => "Searching..."}
                                  filterOption={null}
                                  onInputChange={(inputValue) => {
                                    if (supplierSearchTimeout) {
                                      clearTimeout(supplierSearchTimeout);
                                    }
                                    
                                    // Trim the input value before searching
                                    const trimmedValue = inputValue ? inputValue.trim() : '';
                                    
                                    const timeout = setTimeout(() => {
                                      if (trimmedValue.length >= 3) {
                                        searchSuppliersFilterManually(trimmedValue);
                                      } else {
                                        setSupplierSearchOptions([]);
                                      }
                                    }, 300);
                                    setSupplierSearchTimeout(timeout);
                                    return inputValue;
                                  }}
                                />
                              </Col>
                              <Col md={3}>
                                <Label>From Date</Label>
                                <DatePicker
                                  selected={filter.From_Date}
                                  onChange={(date) => setFilter({ ...filter, From_Date: date })}
                                  dateFormat="dd/MM/yyyy"
                                  className="form-control"
                                  isClearable
                                />
                              </Col>
                              <Col md={3}>
                                <Label>To Date</Label>
                                <DatePicker
                                  selected={filter.To_Date}
                                  onChange={(date) => setFilter({ ...filter, To_Date: date })}
                                  dateFormat="dd/MM/yyyy"
                                  className="form-control"
                                  isClearable
                                />
                              </Col>
                              <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    onClick={handleApplyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={handleClearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Purchase Order List
                            </h5>
                            {hasEditOrHigher && (
                              <Button
                                color="primary"
                                onClick={() => setActiveTab("1")}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add New Purchase Order
                              </Button>
                            )}
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading purchase orders...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="4%">#</th>
                                      <th width="12%">Date</th>
                                      <th width="15%">Order Number</th>
                                      <th width="15%">Supplier</th>
                                      <th width="10%">Net Amount</th>
                                      <th width="10%">Is Received</th>
                                      <th width="10%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedData.length > 0 ? (
                                      paginatedData.map((item, index) => (
                                        <tr key={`purchase-request-${item.purchaseRequest_Id || index}`}>
                                          <td>{startIndex + index + 1}</td>
                                          <td>{item.purchaseRequest_Date ? formatDateDDMMYYYY(item.purchaseRequest_Date) : 'N/A'}</td>
                                          <td>{item.purchaseRequest_OrderNo || 'N/A'}</td>
                                          <td>{item.supplier_NameEn || 'N/A'}</td>
                                          <td>{Number(item.purchaseRequest_NetAmount ?? 0).toFixed(2)}</td>
                                          <td>
                                            <span className={`badge ${item.purchaseRequest_Is_Received ? 'bg-success' : 'bg-warning'}`}>
                                              {item.purchaseRequest_Is_Received ? 'Yes' : 'No'}
                                            </span>
                                          </td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleView(item)}
                                                className="waves-effect waves-light"
                                              >
                                                <Eye size={12} />
                                              </Button>
                                              {hasEditOrHigher && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(item)}
                                                  disabled={editLoadingRow === item.PurchaseRequest_Id}
                                                  className="waves-effect waves-light"
                                                >
                                                  {editLoadingRow === item.PurchaseRequest_Id ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="7" className="text-center">
                                          {hasAppliedFilter ? "No purchase orders found matching the filter criteria." : "No purchase orders available. Please add some purchase orders first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                                   style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                                <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                                  <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                                  Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? startIndex + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{endIndex}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                                </div>
                                {totalPages > 1 && (
                                  <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={page === 1}
                                      onClick={() => handlePageChange(page - 1)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                        color: page === 1 ? '#6c757d' : '#495057'
                                      }}
                                    >
                                      <i className="mdi mdi-chevron-left"></i>
                                    </Button>
                                    
                                    {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                      const pageNum = index + 1;
                                      const isActive = page === pageNum;
                                      
                                      return (
                                        <Button
                                          key={pageNum}
                                          color={isActive ? "primary" : "light"}
                                          size="sm"
                                          onClick={() => handlePageChange(pageNum)}
                                          style={{
                                            border: '1px solid #dee2e6',
                                            borderRadius: '0.375rem',
                                            minWidth: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: isActive ? '#556ee6' : '#fff',
                                            borderColor: isActive ? '#556ee6' : '#dee2e6',
                                            color: isActive ? '#fff' : '#495057',
                                            fontWeight: isActive ? '600' : '400',
                                            boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                          }}
                                        >
                                          {pageNum}
                                        </Button>
                                      );
                                    })}
                                    
                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={page === Math.ceil(totalCount / pageSize)}
                                      onClick={() => handlePageChange(page + 1)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                        color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                                      }}
                                    >
                                      <i className="mdi mdi-chevron-right"></i>
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

